import { useEffect, useRef, useState, useMemo } from "react";
import { Plus, Pencil, Trash2, Package, ShoppingBag, TrendingUp, Loader2, Upload, X, LayoutDashboard, Banknote, BarChart3, Calendar, Phone, Mail, MapPin, Eye, Truck, Printer, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import api, { resolveImage, formatPrice, formatApiErrorDetail } from "@/lib/api";
import { STATUS_LABELS, STATUS_COLORS, STATUS_OPTIONS } from "@/lib/orderStatus";
import { AdminAnalytics } from "@/pages/AdminAnalytics";

const RO_TZ = "Europe/Bucharest";
const fmtDateTimeRO = (iso) =>
  new Date(iso).toLocaleString("ro-RO", { timeZone: RO_TZ, dateStyle: "medium", timeStyle: "short" });
const roDayKey = (iso) => {
  // YYYY-MM-DD in Romania timezone
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: RO_TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
  return parts;
};

const CATEGORIES = ["Detergenți", "Balsam de rufe", "Pachete promo", "Accesorii"];
const BADGE_OPTIONS = ["OFERTĂ", "TRANSPORT GRATUIT", "NOU"];

const emptyProduct = {
  name: "", description: "", price: "", old_price: "", category: "Detergenți",
  images: [], stock: 100, volume: "", scent: "", washes: "", badges: [], benefits: [], featured: false, active: true,
};

function ProductForm({ open, onClose, product, onSaved }) {
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (product) {
      setForm({
        ...emptyProduct, ...product,
        old_price: product.old_price ?? "", washes: product.washes ?? "",
      });
    } else {
      setForm(emptyProduct);
    }
  }, [product, open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleBadge = (b) =>
    setForm((f) => ({ ...f, badges: f.badges.includes(b) ? f.badges.filter((x) => x !== b) : [...f.badges, b] }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.path] }));
      toast.success("Imagine încărcată");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare la încărcare");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (img) => setForm((f) => ({ ...f, images: f.images.filter((i) => i !== img) }));

  const submit = async () => {
    if (!form.name || !form.price) {
      toast.error("Completează numele și prețul");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      old_price: form.old_price ? parseFloat(form.old_price) : null,
      stock: parseInt(form.stock) || 0,
      washes: form.washes ? parseInt(form.washes) : null,
    };
    try {
      if (product) await api.put(`/products/${product.id}`, payload);
      else await api.post("/products", payload);
      toast.success(product ? "Produs actualizat" : "Produs adăugat");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{product ? "Editează produs" : "Adaugă produs"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Imagini produs</Label>
            <div className="mt-2 flex flex-wrap gap-3">
              {form.images.map((img) => (
                <div key={img} className="relative h-24 w-24 overflow-hidden rounded-lg border border-slate-200">
                  <img src={resolveImage(img)} alt="" className="h-full w-full object-contain p-1" />
                  <button onClick={() => removeImage(img)} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-primary hover:text-primary"
                data-testid="upload-image-btn"
              >
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Upload className="h-5 w-5" /><span className="text-xs">Încarcă</span></>}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="image-input" />
            </div>
          </div>

          <div>
            <Label htmlFor="pf-name">Nume produs *</Label>
            <Input id="pf-name" value={form.name} onChange={set("name")} className="mt-1.5" data-testid="pf-name" />
          </div>

          <div>
            <Label htmlFor="pf-desc">Descriere</Label>
            <Textarea id="pf-desc" value={form.description} onChange={set("description")} className="mt-1.5" rows={3} data-testid="pf-description" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pf-price">Preț (lei) *</Label>
              <Input id="pf-price" type="number" step="0.01" value={form.price} onChange={set("price")} className="mt-1.5" data-testid="pf-price" />
            </div>
            <div>
              <Label htmlFor="pf-oldprice">Preț vechi (lei)</Label>
              <Input id="pf-oldprice" type="number" step="0.01" value={form.old_price} onChange={set("old_price")} className="mt-1.5" data-testid="pf-oldprice" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Categorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="pf-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="pf-stock">Stoc</Label>
              <Input id="pf-stock" type="number" value={form.stock} onChange={set("stock")} className="mt-1.5" data-testid="pf-stock" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="pf-volume">Volum</Label>
              <Input id="pf-volume" value={form.volume} onChange={set("volume")} placeholder="5L" className="mt-1.5" data-testid="pf-volume" />
            </div>
            <div>
              <Label htmlFor="pf-scent">Parfum</Label>
              <Input id="pf-scent" value={form.scent} onChange={set("scent")} className="mt-1.5" data-testid="pf-scent" />
            </div>
            <div>
              <Label htmlFor="pf-washes">Spălări</Label>
              <Input id="pf-washes" type="number" value={form.washes} onChange={set("washes")} className="mt-1.5" data-testid="pf-washes" />
            </div>
          </div>

          <div>
            <Label>Etichete</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {BADGE_OPTIONS.map((b) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => toggleBadge(b)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    form.badges.includes(b) ? "border-primary bg-primary text-white" : "border-slate-300 text-slate-600 hover:border-primary"
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="pf-benefits">Beneficii (câte unul pe rând)</Label>
            <Textarea
              id="pf-benefits"
              value={(form.benefits || []).join("\n")}
              onChange={(e) => setForm({ ...form, benefits: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
              rows={4}
              placeholder={"Spălare la 20°-60°\nPână la 125 de spălări\nTexturi fine și moi\nCurățenie garantată"}
              className="mt-1.5"
              data-testid="pf-benefits"
            />
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="pf-featured" />
              <Label className="cursor-pointer">Recomandat</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="pf-active" />
              <Label className="cursor-pointer">Activ (vizibil)</Label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Anulează</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full hover:bg-primary/90" data-testid="pf-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderDetail, setOrderDetail] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [awbOrder, setAwbOrder] = useState(null);
  const [awbForm, setAwbForm] = useState({ weight: 5, parcels: 1, package_content: "Detergenti / produse curatenie", observations: "", postal_code: "" });
  const [awbBusy, setAwbBusy] = useState(false);
  const [labelBusy, setLabelBusy] = useState(null);

  const loadAll = () => {
    Promise.all([
      api.get("/products", { params: { include_inactive: true } }),
      api.get("/orders"),
      api.get("/stats"),
    ]).then(([p, o, s]) => {
      setProducts(p.data); setOrders(o.data); setStats(s.data);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  const confirmDelete = async () => {
    try {
      await api.delete(`/products/${deleteId}`);
      toast.success("Produs șters");
      setDeleteId(null);
      loadAll();
    } catch {
      toast.error("Eroare la ștergere");
    }
  };

  const changeStatus = async (orderId, status) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      toast.success("Status actualizat");
      loadAll();
    } catch {
      toast.error("Eroare");
    }
  };

  const openAwbDialog = (order) => {
    const itemsSummary = (order.items || []).map((i) => `${i.qty}x ${i.name}`).join(", ");
    setAwbForm({ weight: 5, parcels: 1, package_content: "Detergenti / produse curatenie", observations: itemsSummary, postal_code: order.customer?.postal_code || "" });
    setAwbOrder(order);
  };

  const generateAwb = async () => {
    if (!awbOrder) return;
    setAwbBusy(true);
    try {
      const { data } = await api.post(`/orders/${awbOrder.id}/awb`, {
        weight: Number(awbForm.weight) || undefined,
        parcels: Number(awbForm.parcels) || 1,
        package_content: awbForm.package_content,
        observations: awbForm.observations,
        postal_code: awbForm.postal_code,
      });
      toast.success(`AWB generat: ${data.awb?.barcode}`);
      setAwbOrder(null);
      setOrders((prev) => prev.map((o) => (o.id === data.id ? data : o)));
      setOrderDetail((prev) => (prev && prev.id === data.id ? data : prev));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare la generarea AWB");
    } finally {
      setAwbBusy(false);
    }
  };

  const openLabel = async (order) => {
    setLabelBusy(order.id);
    try {
      const res = await api.get(`/orders/${order.id}/awb/label`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error("Nu s-a putut deschide eticheta AWB");
    } finally {
      setLabelBusy(null);
    }
  };

  const downloadLabel = async (order) => {
    setLabelBusy(order.id);
    try {
      const res = await api.get(`/orders/${order.id}/awb/label`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `AWB_${order.awb?.barcode || order.order_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error("Nu s-a putut descărca eticheta AWB");
    } finally {
      setLabelBusy(null);
    }
  };

  const statCards = [
    { label: "Comenzi totale", value: stats.total_orders ?? 0, icon: ShoppingBag },
    { label: "Comenzi azi", value: stats.orders_today ?? 0, icon: TrendingUp },
    { label: "Venit azi", value: formatPrice(stats.revenue_today ?? 0), icon: Banknote },
    { label: "Venit total", value: formatPrice(stats.revenue ?? 0), icon: Banknote },
  ];

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const day = roDayKey(o.created_at);
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      return true;
    });
  }, [orders, dateFrom, dateTo]);

  if (loading) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="admin-page">
      <div className="mb-8 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-white"><LayoutDashboard className="h-5 w-5" /></span>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Panou de administrare</h1>
      </div>

      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`stat-${c.label}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{c.label}</span>
              <c.icon className="h-4 w-4 text-primary" />
            </div>
            <p className="mt-3 font-heading text-2xl font-bold text-slate-900">{c.value}</p>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="products" data-testid="admin-tab-products"><Package className="mr-2 h-4 w-4" /> Produse</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders"><ShoppingBag className="mr-2 h-4 w-4" /> Comenzi</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics"><BarChart3 className="mr-2 h-4 w-4" /> Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="products">
          <div className="mb-4 flex justify-end">
            <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-full hover:bg-primary/90" data-testid="add-product-btn">
              <Plus className="mr-2 h-4 w-4" /> Adaugă produs
            </Button>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produs</TableHead>
                  <TableHead>Categorie</TableHead>
                  <TableHead className="text-right">Preț</TableHead>
                  <TableHead className="text-right">Stoc</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} data-testid={`admin-product-${p.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <img src={resolveImage(p.images?.[0])} alt="" className="h-10 w-10 rounded border border-slate-100 object-contain p-0.5" />
                        <span className="font-medium text-slate-900 line-clamp-1 max-w-[240px]">{p.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{p.category}</TableCell>
                    <TableCell className="text-right font-mono">{formatPrice(p.price)}</TableCell>
                    <TableCell className="text-right font-mono">{p.stock}</TableCell>
                    <TableCell>
                      {p.active ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Activ</Badge>
                        : <Badge variant="secondary">Inactiv</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => { setEditing(p); setFormOpen(true); }} data-testid={`edit-${p.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteId(p.id)} className="text-destructive" data-testid={`delete-${p.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {products.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="py-10 text-center text-slate-500">Niciun produs. Adaugă primul produs.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
            <div>
              <Label htmlFor="date-from" className="flex items-center gap-1.5 text-xs text-slate-500"><Calendar className="h-3.5 w-3.5" /> De la data</Label>
              <Input id="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1.5 w-44" data-testid="order-date-from" />
            </div>
            <div>
              <Label htmlFor="date-to" className="flex items-center gap-1.5 text-xs text-slate-500"><Calendar className="h-3.5 w-3.5" /> Până la data</Label>
              <Input id="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1.5 w-44" data-testid="order-date-to" />
            </div>
            {(dateFrom || dateTo) && (
              <Button variant="outline" className="rounded-full" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="order-date-clear">
                <X className="mr-1 h-4 w-4" /> Resetează
              </Button>
            )}
            <span className="ml-auto text-sm text-slate-500" data-testid="orders-count">{filteredOrders.length} comenzi</span>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Comandă</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Data și ora</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>AWB Cargus</TableHead>
                  <TableHead className="text-right">Detalii</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((o) => (
                  <TableRow key={o.id} data-testid={`admin-order-${o.id}`}>
                    <TableCell className="font-mono font-medium">#{o.order_number}</TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-900">{o.customer.name}</p>
                      <p className="text-xs text-slate-500">{o.customer.phone} · {o.customer.city}</p>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-slate-600">{fmtDateTimeRO(o.created_at)}</TableCell>
                    <TableCell className="text-right font-mono">{formatPrice(o.total)}</TableCell>
                    <TableCell>
                      <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                        <SelectTrigger className="w-36" data-testid={`order-status-${o.id}`}>
                          <SelectValue><Badge className={STATUS_COLORS[o.status]}>{STATUS_LABELS[o.status]}</Badge></SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {o.awb?.barcode ? (
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-xs font-medium text-emerald-700" data-testid={`awb-code-${o.id}`}>{o.awb.barcode}</span>
                          <Button variant="outline" size="sm" className="h-7 rounded-full px-2" onClick={() => openLabel(o)} disabled={labelBusy === o.id} title="Printează" data-testid={`awb-print-${o.id}`}>
                            {labelBusy === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Printer className="h-3.5 w-3.5" />}
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 rounded-full px-2" onClick={() => downloadLabel(o)} disabled={labelBusy === o.id} title="Descarcă PDF" data-testid={`awb-download-${o.id}`}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <Button variant="outline" size="sm" className="h-8 rounded-full" onClick={() => openAwbDialog(o)} data-testid={`awb-generate-${o.id}`}>
                          <Truck className="mr-1.5 h-3.5 w-3.5" /> Generează AWB
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => setOrderDetail(o)} data-testid={`order-detail-${o.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredOrders.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="py-10 text-center text-slate-500">Nicio comandă în intervalul selectat.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="analytics">
          <AdminAnalytics />
        </TabsContent>
      </Tabs>

      <ProductForm open={formOpen} onClose={() => setFormOpen(false)} product={editing} onSaved={loadAll} />

      <Dialog open={!!orderDetail} onOpenChange={(o) => !o && setOrderDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="order-detail-dialog">
          {orderDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Comanda #{orderDetail.order_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3 text-sm">
                  <span className="flex items-center gap-1.5 text-slate-500"><Calendar className="h-4 w-4" /> {fmtDateTimeRO(orderDetail.created_at)}</span>
                  <Badge className={STATUS_COLORS[orderDetail.status]}>{STATUS_LABELS[orderDetail.status]}</Badge>
                </div>

                <div>
                  <h4 className="mb-3 font-heading text-sm font-bold text-slate-900">Date client</h4>
                  <div className="grid grid-cols-1 gap-x-4 gap-y-3 rounded-lg border border-slate-200 p-4 text-sm sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Nume complet</p>
                      <p className="mt-0.5 font-medium text-slate-900" data-testid="detail-customer-name">{orderDetail.customer.name || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Telefon</p>
                      <a href={`tel:${orderDetail.customer.phone}`} className="mt-0.5 flex items-center gap-1.5 font-medium text-primary hover:underline" data-testid="detail-customer-phone"><Phone className="h-3.5 w-3.5" /> {orderDetail.customer.phone || "-"}</a>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Email</p>
                      <p className="mt-0.5 flex items-center gap-1.5 break-all text-slate-700" data-testid="detail-customer-email"><Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" /> {orderDetail.customer.email || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Cod poștal</p>
                      <p className="mt-0.5 text-slate-700" data-testid="detail-customer-postal">{orderDetail.customer.postal_code || "—"}</p>
                    </div>
                    <div className="sm:col-span-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Adresă (stradă, număr)</p>
                      <p className="mt-0.5 flex items-start gap-1.5 text-slate-900" data-testid="detail-customer-address"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" /> {orderDetail.customer.address || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Oraș / Sat</p>
                      <p className="mt-0.5 text-slate-900" data-testid="detail-customer-city">{orderDetail.customer.city || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Județ</p>
                      <p className="mt-0.5 text-slate-900" data-testid="detail-customer-county">{orderDetail.customer.county || "-"}</p>
                    </div>
                    {orderDetail.customer.notes && (
                      <div className="sm:col-span-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Observații client</p>
                        <p className="mt-0.5 rounded bg-amber-50 px-3 py-2 text-amber-800" data-testid="detail-customer-notes">{orderDetail.customer.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 font-heading text-sm font-bold text-slate-900">Produse comandate</h4>
                  <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                    {orderDetail.items.map((it, i) => (
                      <div key={i} className="flex items-center gap-3 p-3">
                        <img src={resolveImage(it.image)} alt="" className="h-10 w-10 rounded border border-slate-100 object-contain p-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900 line-clamp-1">{it.name}</p>
                          <p className="text-xs text-slate-500">{it.qty} × {formatPrice(it.price)}</p>
                        </div>
                        <span className="font-mono text-sm font-semibold">{formatPrice(it.price * it.qty)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-1 border-t border-slate-200 pt-3 text-sm">
                  <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="font-mono">{formatPrice(orderDetail.subtotal)}</span></div>
                  <div className="flex justify-between text-slate-600"><span>Transport</span><span className="font-medium text-emerald-600">Gratuit</span></div>
                  <div className="flex justify-between pt-1 text-base font-bold text-slate-900"><span>Total</span><span className="font-mono text-primary">{formatPrice(orderDetail.total)}</span></div>
                  <p className="pt-2 text-xs text-slate-500">Plată: ramburs la livrare</p>
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <h4 className="mb-2 flex items-center gap-1.5 font-heading text-sm font-bold text-slate-900"><Truck className="h-4 w-4 text-primary" /> Livrare Cargus</h4>
                  {orderDetail.awb?.barcode ? (
                    <div className="flex items-center justify-between rounded-lg bg-emerald-50 px-4 py-3">
                      <div className="text-sm">
                        <p className="font-mono font-semibold text-emerald-800" data-testid="detail-awb-code">AWB: {orderDetail.awb.barcode}</p>
                        <p className="text-xs text-emerald-600">{orderDetail.awb.parcels || 1} colet(e){orderDetail.awb.weight ? ` · ${orderDetail.awb.weight} kg` : ""}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="rounded-full" onClick={() => openLabel(orderDetail)} disabled={labelBusy === orderDetail.id} data-testid="detail-awb-print">
                          {labelBusy === orderDetail.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Printer className="mr-1.5 h-4 w-4" />} Printează
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-full" onClick={() => downloadLabel(orderDetail)} disabled={labelBusy === orderDetail.id} data-testid="detail-awb-download">
                          <Download className="mr-1.5 h-4 w-4" /> Descarcă
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button className="w-full rounded-full" onClick={() => openAwbDialog(orderDetail)} data-testid="detail-awb-generate">
                      <Truck className="mr-2 h-4 w-4" /> Generează AWB Cargus
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!awbOrder} onOpenChange={(o) => !o && !awbBusy && setAwbOrder(null)}>
        <DialogContent className="max-w-md" data-testid="awb-dialog">
          {awbOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Generează AWB — #{awbOrder.order_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  <p className="font-medium text-slate-900">{awbOrder.customer.name}</p>
                  <p className="text-xs">{awbOrder.customer.address}, {awbOrder.customer.city}, {awbOrder.customer.county}</p>
                  <p className="mt-1 text-xs">Ramburs: <span className="font-semibold text-primary">{formatPrice(awbOrder.total)}</span> (plată la livrare)</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="awb-weight">Greutate totală (kg)</Label>
                    <Input id="awb-weight" type="number" min="0.1" step="0.1" value={awbForm.weight} onChange={(e) => setAwbForm({ ...awbForm, weight: e.target.value })} className="mt-1.5" data-testid="awb-weight" />
                  </div>
                  <div>
                    <Label htmlFor="awb-parcels">Nr. colete</Label>
                    <Input id="awb-parcels" type="number" min="1" step="1" value={awbForm.parcels} onChange={(e) => setAwbForm({ ...awbForm, parcels: e.target.value })} className="mt-1.5" data-testid="awb-parcels" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="awb-postal">Cod poștal destinatar <span className="font-normal text-slate-400">(opțional — se completează automat din Cargus)</span></Label>
                  <Input id="awb-postal" value={awbForm.postal_code} onChange={(e) => setAwbForm({ ...awbForm, postal_code: e.target.value })} className="mt-1.5" placeholder="Auto din Cargus" data-testid="awb-postal-code" />
                </div>
                <div>
                  <Label htmlFor="awb-content">Conținut colet</Label>
                  <Input id="awb-content" value={awbForm.package_content} onChange={(e) => setAwbForm({ ...awbForm, package_content: e.target.value })} className="mt-1.5" data-testid="awb-content" />
                </div>
                <div>
                  <Label htmlFor="awb-obs">Observații (ce a comandat clientul)</Label>
                  <Textarea id="awb-obs" rows={2} value={awbForm.observations} onChange={(e) => setAwbForm({ ...awbForm, observations: e.target.value })} className="mt-1.5" data-testid="awb-observations" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-full" onClick={() => setAwbOrder(null)} disabled={awbBusy}>Anulează</Button>
                <Button className="rounded-full" onClick={generateAwb} disabled={awbBusy} data-testid="awb-confirm">
                  {awbBusy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Se generează...</> : <><Truck className="mr-2 h-4 w-4" /> Generează AWB</>}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ștergi produsul?</AlertDialogTitle>
            <AlertDialogDescription>Această acțiune nu poate fi anulată.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete">Șterge</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
